import { useQuery } from '@tanstack/react-query';
import { TableCell, TableRow } from '@/components/ui/table';
import { fetchCustomerInvoices, type CustomerInvoice } from '../api';
import InvoiceRow from './InvoiceRow';
import type { TriageRow } from '../types/triage';

interface InvoiceRowsProps {
  customerName: string;
  asOfDate: string;
  onSelectInvoice: (invoice: CustomerInvoice) => void;
  triageByInvoice?: Map<string, TriageRow>;
}

export default function InvoiceRows({ customerName, asOfDate, onSelectInvoice, triageByInvoice }: InvoiceRowsProps) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['ar-customer-invoices', customerName, asOfDate],
    queryFn: () => fetchCustomerInvoices(customerName, asOfDate),
    staleTime: 1000 * 60 * 5,
  });

  if (isLoading) {
    return (
      <TableRow className='bg-muted/10'>
        <TableCell colSpan={7} className='pl-10 py-3 text-xs text-muted-foreground'>
          Loading invoices from QuickBooks…
        </TableCell>
      </TableRow>
    );
  }

  if (error) {
    return (
      <TableRow className='bg-muted/10'>
        <TableCell colSpan={7} className='pl-10 py-3 text-xs text-red-600'>
          {error instanceof Error ? error.message : 'Failed to load invoices.'}{' '}
          <button type='button' className='underline' onClick={() => refetch()}>
            Retry
          </button>
        </TableCell>
      </TableRow>
    );
  }

  const invoices = data?.invoices ?? [];
  if (invoices.length === 0) {
    return (
      <TableRow className='bg-muted/10'>
        <TableCell colSpan={7} className='pl-10 py-3 text-xs text-muted-foreground'>
          No open invoices returned by QuickBooks for this customer.
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      {invoices.map((inv) => (
        <InvoiceRow
          key={inv.id}
          invoice={inv}
          asOfDate={asOfDate}
          onClick={onSelectInvoice}
          triage={triageByInvoice?.get(inv.docNumber)}
        />
      ))}
    </>
  );
}
